/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

function AsperaFileSelectController(
    this: JS5Object,

    // external deps
    _: JS5Object,
    loginService: JS5Object,
    $q: JS5Object,
    $scope: JS5Object,
    $timeout: JS5Function,

    // internal deps
    AsperaConnectService: JS5Object,
    MediaInfoService: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    UtilsService: JS5Object
) {
    const resetAll = () => {
        this.registeredFileCount = 0;
        this.expectedFileCount = 0;
        this.registeredFiles = {};

        if (!this.maxFileCount) {
            this.maxFileCount = 1;
        }
    };

    this.asperaConnectInitialized = (): boolean => {
        return AsperaConnectService.initialized;
    };

    const onAllFilesRegistered = () => {
        let asperaUploadObjects = _.map(this.registeredFiles, 'asperaFileInfo');

        this.dropHandler(asperaUploadObjects)
            .catch((errorData: any) => {
                NotificationService.createFailureNotification(
                    `Drag and drop failed.`,
                    `${JSON.stringify(errorData)}`,
                    true,
                    -1);
            })
            .finally(() => {
                this.loadingIndicator.show = false;
                resetAll();
            });
    };

    const getRegisteredFile = (fileName: string) => {
        let fileEntry = _.get(this.registeredFiles, fileName);
        if (!fileEntry) {
            this.registeredFiles[fileName] = {
                fileSystemItem: null,
                asperaFileInfo: null
            };
            fileEntry = this.registeredFiles[fileName];
        }
        return fileEntry;
    };

    const registerDroppedFile = (file: JS5Object) => {
        let fileName = UtilsService.getFileNameFromLocalPath(file);
        let fileEntry = getRegisteredFile(fileName);

        if (file.constructor.name === 'File') {
            // this is the DOM's `File` object
            fileEntry.fileSystemItem = file;
        } else {
            // this is Aspera's file object
            fileEntry.asperaFileInfo = file;
        }

        if (_.get(fileEntry, 'fileSystemItem') && _.get(fileEntry, 'asperaFileInfo')) {
            fileEntry.asperaFileInfo.file = fileEntry.fileSystemItem;
            this.registeredFileCount++;
        }
    };

    const validateDOMAndAsperaFileCounts = (files: JS5Object[]) => {
        let validated = true;
        if (files.length !== this.expectedFileCount) {
            NotificationService.createFailureNotification(
                `Error: There was a problem adding the dropped files.`);
            validated = false;
            console.error(`DOM and Aspera file drop counts don't match`);
        }
        return validated;
    };

    const validateMaxFileCount = (files: JS5Object[]) => {
        let validated = true;
        if (files.length > this.maxFileCount) {
            NotificationService.createFailureNotification(
                `Error: This drop zone accepts at most ${this.maxFileCount} file(s) at a time.`);
            validated = false;
        }
        return validated;
    };

    const validateFileExtensions = (files: JS5Object[]) => {
        let validated = true;

        let fileNames = _.map(files, 'name');
        _.forEach(fileNames, (fileName: JS5Object) => {
            let fileExtension = UtilsService.getFileExtension(fileName);
            // TODO: AB#88420 Use UtilsService regex to check any capitalization
            let fileExtensionIsValid = _.includes(this.fileExtensions, fileExtension);
            validated = validated && fileExtensionIsValid;
        });

        if (!validated) {
            NotificationService.createFailureNotification(
                `Error: This drop zone only accepts files with the following extensions: ` +
                `[ ${UtilsService.filtersToPrettyString(this.fileExtensions)} ]`);
        }

        return validated;
    };

    const validateDroppedFiles = (files: JS5Object[]) => {
        let validated = true;

        if (this.expectedFileCount !== 0) {
            validated =
                validateDOMAndAsperaFileCounts(files) &&
                validateMaxFileCount(files) &&
                validateFileExtensions(files);
        } else {
            this.expectedFileCount = files.length;
        }
        return validated;
    };

    //
    // Called twice per drag & drop event - once to handle the DOM
    // event and once to handle the Aspera event, both for the
    // same files. See the two wrapper handlers below; we need data
    // from both events, and we use this to capture that data.
    //
    const commonDropHandler = ($event: DragEvent, fileContainer: JS5Object, filesPropPath: string) => {
        if (_.get($event, 'type') === 'drop') {
            this.loadingIndicator.show = true;

            $event.stopPropagation();
            $event.preventDefault();

            let files = _.chain(fileContainer).get(filesPropPath).toArray().value();

            if (validateDroppedFiles(files)) {
                _.forEach(files, registerDroppedFile);

                if (this.registeredFileCount === this.expectedFileCount) {
                    onAllFilesRegistered();
                }
            } else {
                this.loadingIndicator.show = false;
                resetAll();
            }
        }
    };

    //
    // Aspera Connect sends an event like this:
    //
    //  {
    //      event: <DragEvent>{
    //      },
    //      files: {
    //          dataTransfer: {
    //              files: <Object>[...]
    //      }
    //  }
    //
    // The files.dataTransfer.files property is NOT an array of
    // File objects, but rather just descriptors with a subset of
    // the properties of the JS File class.
    //
    // The DragEvent has a dataTransfer property but it is empty.
    //
    // Thus we cannot use information from this event to read the
    // contents of files. However we still need it for use with
    // Aspera Connect.
    //
    const asperaDropEventHandler = ($asperaEvent: JS5Object) => {
        let $event: DragEvent = _.get($asperaEvent, 'event');
        commonDropHandler($event, $asperaEvent, 'files.dataTransfer.files');
    };

    //
    // DOM sends an event like this:
    //
    //  <DragEvent>{
    //      ...
    //      dataTransfer: {
    //          files: <File>[...]
    //      },
    //      ...
    //  }
    //
    // We need these file objects, sent from the DOM, because we
    // can read their contents. We can't do that with the objects
    // that Aspera sends.
    //
    const domDropEventHandler = ($event: DragEvent) => {
        commonDropHandler($event, $event, 'dataTransfer.files');
    };

    this.getToolTip = (): string => {
        let tooltip = ``;

        if (!this.asperaConnectInitialized()) {
            tooltip = `AsperaConnect<br/> is not initialized`;
        } else {
            let validExtensions = '(any)';
            if (_.size(this.fileExtensions)) {
                validExtensions = `[${UtilsService.filtersToPrettyString(this.fileExtensions)}]`
            }

            tooltip = `Accepted extensions: ${validExtensions}`;
        }

        return tooltip;
    };

    this.$onInit = () => {
        resetAll();

        this.loadingIndicator = {
            backdrop: false,
            show: false,
            size: 'lg',
            title: '',
            message: ''
        };
    };

    this.$postLink = () => {
        $timeout(() => {
            // add standard DOM event listener
            let dropZone = document.getElementById(this.dropZoneId);
            dropZone.addEventListener('drop', domDropEventHandler, false);

            // add Aspera's proprietary event listener
            AsperaConnectService.setDropTarget(this.dropZoneId, asperaDropEventHandler);
        });
    };
}

angular.module('prism-ui.components').component('asperaFileSelect', {
    controller: AsperaFileSelectController,
    templateUrl: 'components/asperaConnect/aspera-file-select-template.html',
    bindings: {
        dropHandler: '<',
        dropZoneId: '<',
        dropZoneSize: '<',
        fileExtensions: '<',
        maxFileCount: '<',
        showDropZone: '<',
    }
});
